import React, { useEffect, useState } from "react";
import { PageContainer, PageHeader, StatCard, Card } from "./Shared";
import { adminApi } from "./api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, BarChart, Bar } from "recharts";

export default function Analytics() {
  const [days, setDays] = useState("30");
  const [s, setS] = useState(null);

  useEffect(() => {
    adminApi.get(`/admin/analytics/summary?days=${days}`).then((r) => setS(r.data));
  }, [days]);

  return (
    <PageContainer>
      <PageHeader title="Analytics" subtitle="Site traffic, leads, and engagement."
        action={
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        } />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="analytics-stats">
        <StatCard label="Page views" value={s?.total_views ?? "—"} />
        <StatCard label="Unique visitors" value={s?.unique_visitors ?? "—"} />
        <StatCard label="Contact leads" value={s?.contact_leads ?? "—"} />
        <StatCard label="Career apps" value={s?.career_applications ?? "—"} />
        <StatCard label="Chat sessions" value={s?.chat_sessions ?? "—"} />
      </div>

      <Card className="p-6 mt-5">
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#6B7A8D] font-semibold mb-3">Daily traffic</div>
        <div className="h-[260px]" data-testid="analytics-daily-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={s?.daily || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis dataKey="day" stroke="#94A3B8" fontSize={11} />
              <YAxis stroke="#94A3B8" fontSize={11} />
              <Tooltip />
              <Line type="monotone" dataKey="views" stroke="#0F2A6E" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="uniques" stroke="#1B6FE0" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-4 mt-5">
        <Card className="p-6">
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#6B7A8D] font-semibold mb-3">Top sections</div>
          <div className="h-[220px]" data-testid="analytics-sections">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={s?.top_sections || []} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                <XAxis type="number" stroke="#94A3B8" fontSize={11} />
                <YAxis type="category" dataKey="section" stroke="#94A3B8" fontSize={11} width={80} />
                <Tooltip />
                <Bar dataKey="count" fill="#1B6FE0" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6">
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#6B7A8D] font-semibold mb-3">Top paths</div>
          <ul className="space-y-2" data-testid="analytics-paths">
            {(s?.top_paths || []).map((p, i) => (
              <li key={i} className="flex items-center justify-between text-[13.5px]">
                <span className="text-[#1C2B3A] truncate">{p.path}</span>
                <span className="text-[#6B7A8D] tabular-nums">{p.count}</span>
              </li>
            ))}
            {(!s?.top_paths || s.top_paths.length === 0) && <li className="text-[#6B7A8D] text-[13px]">No data yet.</li>}
          </ul>
        </Card>
      </div>
    </PageContainer>
  );
}
